/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { Button, Input, Label, Text, Box } from "@nordcloud/gnui";
import { Customer } from "~/components/BasicCustomerInfo/types";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { LabelWithTooltip } from "~/components/Forms/LabelWithTooltip/LabelWithTooltip";
import { DetailsTitle } from "~/components/Styled/Common";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import {
  useAxios,
  generateActionSuccessText,
  displayCurrencySymbol,
  useShowComponent,
  isValidPrice,
  ValidationMessagePrice,
  isValidTime,
  ValidationMessageTime,
  checkValue,
} from "~/tools";
import { BillingGranularity } from "./BillingGranularity";

type BillingRulesFormCustomerProps = {
  customer: Customer;
  refetch: () => void;
  canEdit?: boolean;
};

type Values = {
  baseManagementFee: string;
  incidentsOffice: string;
  outsideOfficeHoursAfterMidnight: string;
  outsideOfficeHoursBeforeMidnight: string;
  serviceRequestsOffice: string;
  serviceRequestsOutsideOfficeHoursAfterMidnight: string;
  serviceRequestsOutsideOfficeHoursBeforeMidnight: string;
  hoursOfCSM: string;
  hoursOfMC: string;
};

const StyledFieldset = styled(Fieldset)`
  width: 18rem;
`;

const InputStyled = styled(Input)`
  width: 16rem;
`;

// eslint-disable-next-line sonarjs/cognitive-complexity
function BillingRulesFormCustomer({
  customer,
  refetch,
  canEdit = true,
}: BillingRulesFormCustomerProps) {
  const [serviceBilling, setServiceBilling] = React.useState(
    customer?.billingRules?.serviceRequests?.billingGranularity || "60_MIN"
  );
  const [incidentsBilling, setIncidentsBilling] = React.useState(
    customer?.billingRules?.incidents?.billingGranularity || "60_MIN"
  );
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Values) => {
    const organizeValues = {
      ...customer,
      billingRules: {
        baseManagementFee: checkValue(values.baseManagementFee),
        serviceRequests: {
          billingGranularity: serviceBilling,
          officeHours: {
            defaultPricePerHour: checkValue(values.serviceRequestsOffice),
          },
          outsideOfficeHoursAfterMidnight: {
            defaultPricePerHour: checkValue(
              values.serviceRequestsOutsideOfficeHoursAfterMidnight
            ),
          },
          outsideOfficeHoursBeforeMidnight: {
            defaultPricePerHour: checkValue(
              values.serviceRequestsOutsideOfficeHoursBeforeMidnight
            ),
          },
        },
        incidents: {
          billingGranularity: incidentsBilling,
          officeHours: {
            defaultPricePerHour: checkValue(values.incidentsOffice),
          },
          outsideOfficeHoursAfterMidnight: {
            defaultPricePerHour: checkValue(
              values.outsideOfficeHoursAfterMidnight
            ),
          },
          outsideOfficeHoursBeforeMidnight: {
            defaultPricePerHour: checkValue(
              values.outsideOfficeHoursBeforeMidnight
            ),
          },
        },
        hoursIncludedInFixedFee: {
          hoursOfCsm: checkValue(values.hoursOfCSM),
          hoursOfMcEngineer: checkValue(values.hoursOfMC),
        },
      },
      name: customer.name,
    };
    try {
      await Axios().put(`/customers/${customer.id}`, organizeValues);
      showSuccess(generateActionSuccessText(`Billing Rules`)()("updated")());
    } catch (e) {
      showError(e.response);
    } finally {
      refetch();
    }
  };
  const { register, handleSubmit, errors, watch } = useForm();
  const watcher = watch();

  const InputSettings = register({
    validate: isValidPrice,
  });

  React.useEffect(() => {
    customer?.billingRules?.serviceRequests?.billingGranularity &&
      setServiceBilling(
        customer?.billingRules?.serviceRequests?.billingGranularity
      );
    customer?.billingRules?.incidents?.billingGranularity &&
      setIncidentsBilling(
        customer?.billingRules?.incidents?.billingGranularity
      );
  }, [customer, setServiceBilling, setIncidentsBilling]);

  const showSaveBaseFee = useShowComponent(
    [watcher.baseManagementFee],
    [customer?.billingRules?.baseManagementFee]
  );

  const showSaveHours = useShowComponent(
    [watcher.hoursOfCSM, watcher.hoursOfMC],
    [
      customer?.billingRules?.hoursIncludedInFixedFee?.hoursOfCsm,
      customer?.billingRules?.hoursIncludedInFixedFee?.hoursOfMcEngineer,
    ]
  );

  const showSaveTM = useShowComponent(
    [
      watcher.serviceRequestsOffice,
      watcher.serviceRequestsOutsideOfficeHoursBeforeMidnight,
      watcher.serviceRequestsOutsideOfficeHoursAfterMidnight,
      serviceBilling,
      watcher.incidentsOffice,
      watcher.outsideOfficeHoursBeforeMidnight,
      watcher.outsideOfficeHoursAfterMidnight,
      incidentsBilling,
    ],
    [
      customer?.billingRules?.serviceRequests?.officeHours?.defaultPricePerHour,
      customer?.billingRules?.serviceRequests?.outsideOfficeHoursBeforeMidnight
        ?.defaultPricePerHour,
      customer?.billingRules?.serviceRequests?.outsideOfficeHoursAfterMidnight
        ?.defaultPricePerHour,
      customer?.billingRules?.serviceRequests?.billingGranularity,
      customer?.billingRules?.incidents.officeHours?.defaultPricePerHour,
      customer?.billingRules?.incidents?.outsideOfficeHoursBeforeMidnight
        ?.defaultPricePerHour,
      customer?.billingRules?.incidents?.outsideOfficeHoursAfterMidnight
        ?.defaultPricePerHour,
      customer?.billingRules?.incidents?.billingGranularity,
    ]
  );

  return (
    <>
      <Box spacing="spacing04">
        <Text isTitle weight="medium">
          Base fee for Managed Cloud Environments
        </Text>
        <Row css={{ marginTop: "1rem", marginBottom: "0rem" }}>
          <Col xs={4} sm={4} md={4} lg={6}>
            <StyledFieldset css={{ marginTop: "0.5rem" }}>
              <LabelWithTooltip
                tooltipText="Base fee for Managed Cloud Environments"
                labelName="Base fee"
              />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="baseManagementFee"
                  defaultValue={customer?.billingRules?.baseManagementFee}
                  placeholder="price"
                  status={errors.baseManagementFee && "danger"}
                  ref={InputSettings}
                  data-cy="base-fee"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  {displayCurrencySymbol(customer?.info?.currency)}
                </Text>
              </FlexContainer>
              {errors.baseManagementFee && <ValidationMessagePrice />}
            </StyledFieldset>
          </Col>
        </Row>
        {canEdit && (
          <Button
            disabled={!showSaveBaseFee}
            onClick={handleSubmit(onSubmit)}
            data-cy="fee-save-button"
          >
            Save
          </Button>
        )}
      </Box>
      <Box spacing="spacing04">
        <Text isTitle weight="medium">
          Time & Material
        </Text>
        <Row>
          <Col xs={4} sm={4} md={4} lg={6}>
            <DetailsTitle css={{ margin: "0.5rem 0 1rem 0rem" }}>
              Service Request
            </DetailsTitle>
            <StyledFieldset>
              <Label name="Office Hours" />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="serviceRequestsOffice"
                  defaultValue={
                    customer?.billingRules?.serviceRequests?.officeHours
                      ?.defaultPricePerHour
                  }
                  placeholder="price/h"
                  status={errors.serviceRequestsOffice && "danger"}
                  ref={InputSettings}
                  data-cy="service-office-hours"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  {displayCurrencySymbol(customer?.info?.currency)}/h
                </Text>
              </FlexContainer>
              {errors.serviceRequestsOffice && <ValidationMessagePrice />}
            </StyledFieldset>
            <StyledFieldset>
              <Label name="Outside Office Hours - Till 00:00" />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="serviceRequestsOutsideOfficeHoursBeforeMidnight"
                  defaultValue={
                    customer?.billingRules?.serviceRequests
                      ?.outsideOfficeHoursBeforeMidnight?.defaultPricePerHour
                  }
                  placeholder="price/h"
                  status={
                    errors.serviceRequestsOutsideOfficeHoursBeforeMidnight &&
                    "danger"
                  }
                  ref={InputSettings}
                  data-cy="service-outside-00-office-hours"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  {displayCurrencySymbol(customer?.info?.currency)}/h
                </Text>
              </FlexContainer>
              {errors.serviceRequestsOutsideOfficeHoursBeforeMidnight && (
                <ValidationMessagePrice />
              )}
            </StyledFieldset>
            <StyledFieldset>
              <Label name="Outside Office Hours - After 00:00" />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="serviceRequestsOutsideOfficeHoursAfterMidnight"
                  defaultValue={
                    customer?.billingRules?.serviceRequests
                      ?.outsideOfficeHoursAfterMidnight?.defaultPricePerHour
                  }
                  placeholder="price/h"
                  status={
                    errors.serviceRequestsOutsideOfficeHoursAfterMidnight &&
                    "danger"
                  }
                  ref={InputSettings}
                  data-cy="service-after-00-office-hours"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  {displayCurrencySymbol(customer?.info?.currency)}/h
                </Text>
              </FlexContainer>
              {errors.serviceRequestsOutsideOfficeHoursAfterMidnight && (
                <ValidationMessagePrice />
              )}
            </StyledFieldset>
            <BillingGranularity
              name="serviceBilling"
              setUnit={setServiceBilling}
              unit={serviceBilling}
              canEdit={canEdit}
            />
          </Col>
          <Col xs={4} sm={4} md={4} lg={6}>
            <DetailsTitle css={{ margin: "0.5rem 0 1rem 0rem" }}>
              Incident
            </DetailsTitle>
            <StyledFieldset>
              <Label name="Office Hours" />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="incidentsOffice"
                  defaultValue={
                    customer?.billingRules?.incidents?.officeHours
                      ?.defaultPricePerHour
                  }
                  placeholder="price/h"
                  status={errors.incidentsOffice && "danger"}
                  ref={InputSettings}
                  data-cy="incident-office-hours"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  {displayCurrencySymbol(customer?.info?.currency)}/h
                </Text>
              </FlexContainer>
              {errors.incidentsOffice && <ValidationMessagePrice />}
            </StyledFieldset>
            <StyledFieldset>
              <Label name="Outside Office Hours - Till 00:00" />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="outsideOfficeHoursBeforeMidnight"
                  defaultValue={
                    customer?.billingRules?.incidents
                      ?.outsideOfficeHoursBeforeMidnight?.defaultPricePerHour
                  }
                  placeholder="price/h"
                  status={errors.outsideOfficeHoursBeforeMidnight && "danger"}
                  ref={InputSettings}
                  data-cy="incident-outside-office-hours"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  {displayCurrencySymbol(customer?.info?.currency)}/h
                </Text>
              </FlexContainer>
              {errors.outsideOfficeHoursBeforeMidnight && (
                <ValidationMessagePrice />
              )}
            </StyledFieldset>
            <StyledFieldset>
              <Label name="Outside Office Hours - After 00:00" />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="outsideOfficeHoursAfterMidnight"
                  defaultValue={
                    customer?.billingRules?.incidents
                      ?.outsideOfficeHoursAfterMidnight?.defaultPricePerHour
                  }
                  placeholder="price/h"
                  status={errors.outsideOfficeHoursAfterMidnight && "danger"}
                  ref={InputSettings}
                  data-cy="incident-after-00-office-hours"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  {displayCurrencySymbol(customer?.info?.currency)}/h
                </Text>
              </FlexContainer>
              {errors.outsideOfficeHoursAfterMidnight && (
                <ValidationMessagePrice />
              )}
            </StyledFieldset>
            <BillingGranularity
              name="incidentsBillingGranularity"
              setUnit={setIncidentsBilling}
              unit={incidentsBilling}
              data-cy="incidents-billing-units"
              canEdit={canEdit}
            />
          </Col>
        </Row>
        {canEdit && (
          <Button
            disabled={!showSaveTM}
            css={{ marginTop: "2rem" }}
            onClick={handleSubmit(onSubmit)}
            data-cy="time-and-material-save"
          >
            Save
          </Button>
        )}
      </Box>
      <Box spacing="spacing04">
        <Text isTitle weight="medium">
          Hours of CSM/MC Engineer included
        </Text>
        <Row css={{ marginTop: "1rem" }}>
          <Col xs={4} sm={4} md={4} lg={6}>
            <StyledFieldset css={{ marginTop: "0.5rem" }}>
              <LabelWithTooltip
                tooltipText="Number of hours of CSM included in fixed fee"
                labelName="Hours of CSM included"
              />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="hoursOfCSM"
                  defaultValue={
                    customer?.billingRules?.hoursIncludedInFixedFee?.hoursOfCsm
                  }
                  placeholder="Hours of CSM included"
                  status={errors.hoursOfCSM && "danger"}
                  ref={register({ validate: isValidTime })}
                  data-cy="csm-hours-included"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  hrs
                </Text>
              </FlexContainer>
              {errors.hoursOfCSM && <ValidationMessageTime />}
            </StyledFieldset>
            <StyledFieldset>
              <LabelWithTooltip
                tooltipText="Number of hours of MC Engineer included in fixed fee"
                labelName="Hours of MC Engineer included"
              />
              <FlexContainer>
                <InputStyled
                  type="number"
                  name="hoursOfMC"
                  defaultValue={
                    customer?.billingRules?.hoursIncludedInFixedFee
                      ?.hoursOfMcEngineer
                  }
                  status={errors.hoursOfMC && "danger"}
                  placeholder="Hours of MC Engineer included"
                  ref={register({ validate: isValidTime })}
                  data-cy="mc-hours-included"
                  disabled={!canEdit}
                />
                <Text tag="div" css={{ marginLeft: "1rem" }}>
                  hrs
                </Text>
              </FlexContainer>
              {errors.hoursOfMC && <ValidationMessageTime />}
            </StyledFieldset>
          </Col>
        </Row>
        {canEdit && (
          <Button
            disabled={!showSaveHours}
            onClick={handleSubmit(onSubmit)}
            data-cy="csm-mc-save"
          >
            Save
          </Button>
        )}
      </Box>
    </>
  );
}

export { BillingRulesFormCustomer };
